import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Upload,
  Files,
  ArrowUpRight,
  Clock,
  Check,
  CheckCircle2,
  Music2,
  X,
  User,
  Plus,
  TrendingUp,
} from 'lucide-react';
import { UserCircle } from 'phosphor-react';
import api from '../services/api';
import CatalogSearch from '../components/CatalogSearch';

function useRollingNumber(target, duration = 900, delay = 0) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(0);
  const prevTarget = useRef(0);

  useEffect(() => {
    let start = null;
    const from = prevTarget.current;

    const tick = (ts) => {
      if (start === null) start = ts;
      const elapsed = ts - start - delay;
      if (elapsed < 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const val = Math.round(from + (target - from) * eased);
      setDisplay(val);
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      prevTarget.current = target;
    };
  }, [target, duration, delay]);

  return display;
}

function StatCard({ label, value, icon, tone = 'default', delay = 0, animate = true }) {
  const numValue = typeof value === 'number' ? value : parseInt(value, 10) || 0;
  const displayValue = useRollingNumber(animate ? numValue : numValue, 1100, delay + 100);

  const toneClass =
    tone === 'ok' ? 'text-[#39FF14]' : tone === 'warn' ? 'text-[#C4AB04]' : 'text-white';
  const glowColor =
    tone === 'ok'
      ? 'rgba(57,255,20,0.18)'
      : tone === 'warn'
        ? 'rgba(196,171,4,0.18)'
        : 'rgba(255,255,255,0.06)';
  const barColor = tone === 'ok' ? 'bg-[#39FF14]' : tone === 'warn' ? 'bg-[#C4AB04]' : 'bg-white/60';
  const barGlow =
    tone === 'ok'
      ? 'shadow-[0_0_8px_rgba(57,255,20,0.4)]'
      : tone === 'warn'
        ? 'shadow-[0_0_8px_rgba(196,171,4,0.4)]'
        : '';

  const pct = animate ? Math.min(displayValue / (numValue * 1.3 || 10), 1) : 0.75;

  return (
    <div
      className="bg-black/90 rounded-lg p-5 pb-7 shadow-[0_4px_4px_rgba(0,0,0,0.17)] hover:bg-black/60 hover:scale-[1.02] transition-all duration-300 ease-out shimmer-card stat-card-wake relative overflow-hidden"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className="absolute bottom-0 right-0 w-24 h-24 rounded-full opacity-20 blur-2xl pointer-events-none translate-x-4 translate-y-4"
        style={{ background: glowColor }}
      />

      <div className="flex items-center gap-2">
        <div className={toneClass}>{icon}</div>
        <div className="text-xs font-bold uppercase tracking-wider text-white">{label}</div>
      </div>

      <div className="mt-3">
        <div
          className="text-2xl md:text-3xl font-bold text-white tabular-nums counter-wake"
          style={{ animationDelay: `${delay + 80}ms` }}
        >
          {displayValue.toLocaleString()}
        </div>
      </div>

      <div className="absolute bottom-3 left-5 right-5 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className={`h-full rounded-full ${barColor} ${barGlow}`}
          style={{
            width: `${pct * 100}%`,
            transition: `width ${1100 + delay}ms cubic-bezier(0.34,1.1,0.64,1) ${delay + 80}ms`,
          }}
        />
      </div>
    </div>
  );
}

function QuickAction({ title, description, icon, href }) {
  return (
    <Link
      to={href}
      className="bg-black/90 rounded-lg p-6 text-left shadow-[0_4px_4px_rgba(0,0,0,0.17)] hover:bg-black/60 hover:scale-[1.02] transition-all duration-300 ease-out shimmer-card group flex flex-col h-full min-h-[180px]"
    >
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded bg-black border border-white/15 flex items-center justify-center text-white shrink-0">
          {icon}
        </div>
        <div className="text-xs font-bold uppercase tracking-wider text-white">{title}</div>
      </div>
      <div className="mt-3 text-xs text-gray-400 leading-relaxed flex-1">{description}</div>
      <div className="mt-auto pt-4 flex justify-end">
        <span className="inline-flex items-center gap-1 bg-transparent border border-white/30 text-white/60 text-xs font-medium px-3 py-1.5 rounded uppercase tracking-wider group-hover:border-white group-hover:text-white group-hover:bg-white/[0.06] transition-all duration-200">
          OPEN <ArrowUpRight size={12} />
        </span>
      </div>
    </Link>
  );
}

function ActivityIcon({ type }) {
  if (type === 'track_submission') return <Music2 size={16} className="text-[#C4AB04]" />;
  if (type === 'artist_submission') return <User size={16} className="text-[#C4AB04]" />;
  if (type === 'track_approval' || type === 'artist_approval') {
    return <Check size={16} className="text-[#39FF14]" />;
  }
  if (type === 'track_rejection' || type === 'artist_rejection') {
    return <X size={16} className="text-[#FF003C]" />;
  }
  return <Clock size={16} className="text-gray-400" />;
}

function activityLabel(activity) {
  if (activity.type === 'track_submission') return `Track "${activity.title}" submitted`;
  if (activity.type === 'artist_submission') return `Artist "${activity.artist_name}" submitted`;
  if (activity.type === 'track_approval') return `Track "${activity.title}" approved`;
  if (activity.type === 'track_rejection') return `Track "${activity.title}" rejected`;
  if (activity.type === 'artist_approval') return `Artist "${activity.artist_name}" approved`;
  if (activity.type === 'artist_rejection') return `Artist "${activity.artist_name}" rejected`;
  return 'Activity update';
}

function activityStatusTone(type) {
  if (type === 'track_approval' || type === 'artist_approval') return 'ok';
  if (type === 'track_rejection' || type === 'artist_rejection') return 'danger';
  if (type === 'track_submission' || type === 'artist_submission') return 'warn';
  return 'default';
}

function ActivityStatusBadge({ type }) {
  const tone = activityStatusTone(type);
  const label =
    tone === 'ok' ? 'Approved' : tone === 'danger' ? 'Rejected' : tone === 'warn' ? 'Submitted' : 'Update';
  const cls =
    tone === 'ok'
      ? 'text-[#39FF14]'
      : tone === 'danger'
        ? 'text-[#FF003C]'
        : tone === 'warn'
          ? 'text-[#C4AB04]'
          : 'text-gray-400';

  return (
    <span className={`bg-white/[0.17] px-2 py-1 rounded text-xs font-bold uppercase tracking-wider ${cls}`}>
      {label}
    </span>
  );
}

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/users/catalog-stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  const fetchRecentActivity = useCallback(async () => {
    try {
      setActivityLoading(true);
      const endpoint =
        user?.role === 'admin' ? '/api/submissions/admin/recent-activity' : '/api/submissions/recent-activity';
      const response = await api.get(endpoint);
      setRecentActivity(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchStats();
    fetchRecentActivity();
  }, [fetchStats, fetchRecentActivity]);

  const quickActions = [
    {
      title: 'Batch/Bulk Upload',
      description: 'Map metadata from a CSV file and upload matching audio and image files.',
      icon: <Files size={20} />,
      href: '/upload',
    },
    {
      title: 'Single Upload',
      description: 'Upload individual tracks with metadata and audio files.',
      icon: <Upload size={20} />,
      href: '/upload?mode=single',
    },
    {
      title: user?.role === 'admin' ? 'View All Submissions' : 'View Submissions',
      description:
        user?.role === 'admin'
          ? 'Review and manage submissions across all users.'
          : 'Track status and manage your existing submissions.',
      icon: <TrendingUp size={20} />,
      href: '/submissions',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-50"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in relative">
      <div className="absolute top-6 right-6 z-10 flex items-center gap-3">
        <Link
          to="/upload"
          className="bg-transparent border border-white/30 text-white/70 text-xs font-medium px-3 py-2 rounded uppercase tracking-wider hover:border-white hover:text-white hover:bg-white/[0.06] transition-all duration-200 flex items-center gap-2"
        >
          <Upload size={14} /> Import CSV
        </Link>
        <Link
          to="/upload?mode=single"
          className="bg-transparent border border-white/30 text-white/70 text-xs font-medium px-3 py-2 rounded uppercase tracking-wider hover:border-white hover:text-white hover:bg-white/[0.06] transition-all duration-200 flex items-center gap-2"
        >
          <Plus size={14} /> Add Track
        </Link>
      </div>

      <div className="space-y-6">
        <div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">MUSIC</div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-balance">Catalog Management</h1>
          </div>
        </div>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 queue-fade-in">
          <StatCard
            label="Total Tracks"
            value={stats?.total_tracks || 0}
            icon={<Music2 size={18} />}
            tone="default"
            delay={0}
            animate={initialized}
          />
          <StatCard
            label="In Review"
            value={stats?.in_review || 0}
            icon={<Clock size={18} />}
            tone="warn"
            delay={120}
            animate={initialized}
          />
          <StatCard
            label="Approved Today"
            value={stats?.approved_today || 0}
            icon={<CheckCircle2 size={18} />}
            tone="ok"
            delay={240}
            animate={initialized}
          />
          <StatCard
            label="Artists"
            value={stats?.artist_total || 0}
            icon={<UserCircle size={18} weight="fill" />}
            tone="default"
            delay={360}
            animate={initialized}
          />
        </section>

        <CatalogSearch isAdmin={user?.role === 'admin'} />

        <div className="border-t border-white/8 my-2 opacity-50" />

        <section>
          <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">QUICK ACTIONS</div>
          <div className="grid lg:grid-cols-3 gap-3 md:gap-6">
            {quickActions.map((action) => (
              <QuickAction key={action.href} {...action} />
            ))}
          </div>
        </section>

        <section className="bg-black/90 rounded-lg shadow-[0_4px_4px_rgba(0,0,0,0.17)] overflow-hidden">
          <div className="px-6 py-4 border-b border-white/15 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-white">RECENT ACTIVITY</div>
            <Link
              to="/submissions"
              className="text-xs font-bold uppercase tracking-wider text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
            >
              View all <ArrowUpRight size={14} />
            </Link>
          </div>

          {activityLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold-50"></div>
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <h4 className="text-sm font-medium text-gray-400 mb-2 text-center">No recent activity</h4>
              <p className="text-xs text-gray-500 text-center">
                {user?.role === 'admin'
                  ? 'No submissions or approvals found'
                  : 'Start by uploading your first track'}
              </p>
            </div>
          ) : (
            <ul>
              {recentActivity.map((activity, index) => (
                <li
                  key={`${activity.type}-${activity.created_at}-${index}`}
                  className="px-6 py-4 flex items-center gap-4 hover:bg-black/60 transition-colors border-b border-white/[0.04] last:border-b-0"
                >
                  <div className="w-9 h-9 rounded bg-white/[0.04] flex items-center justify-center shrink-0">
                    <ActivityIcon type={activity.type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{activityLabel(activity)}</div>
                    <div className="text-xs text-gray-400 mt-0.5 truncate">
                      {activity.user_email && user?.role === 'admin' && (
                        <span className="mr-2">by {activity.user_email}</span>
                      )}
                      <span>
                        {new Date(activity.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                  <ActivityStatusBadge type={activity.type} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
